import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Truck, Search, Package, Calendar, ChevronRight,
  CheckCircle, Clock, AlertCircle, Building2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogTitle,
} from '@/components/ui/dialog';
import useClienteActivo from '@/hooks/useClienteActivo';
import ClienteActivoSelector from '@/components/ClienteActivoSelector';
import { fetchEntregasClientes } from '@/services/entregasClientes.service';

const ESTADO_CONFIG = {
  'Pendiente Verificacion': { label: 'Pendiente verificación', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  'Verificado': { label: 'Verificado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  'Cancelado': { label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: AlertCircle },
  'default': { label: 'En proceso', color: 'bg-blue-100 text-blue-800', icon: Clock },
};

const ITEMS_PER_PAGE = 20;

const ClientePrendasRecolecciones = () => {
  const navigate = useNavigate();
  const {
    clientesAsignados,
    clienteActivoId,
    setClienteActivo,
  } = useClienteActivo();

  const [recolecciones, setRecolecciones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selected, setSelected] = useState(null);

  const sinClientes = clientesAsignados.length === 0;

  const loadData = useCallback(async () => {
    if (!clienteActivoId) return;
    setIsLoading(true);
    try {
      const response = await fetchEntregasClientes({
        query: { 'cliente.id': clienteActivoId, tipo: 'Recoleccion' },
        limit: ITEMS_PER_PAGE,
        page,
      });
      setRecolecciones(response.data || []);
      setTotalRecords(response.totalRecords || 0);
    } catch {
      setRecolecciones([]);
    } finally {
      setIsLoading(false);
    }
  }, [clienteActivoId, page]);

  useEffect(() => { loadData(); }, [loadData]);

  // Reset a la primera página si cambia el cliente activo
  useEffect(() => { setPage(1); }, [clienteActivoId]);

  const filtered = recolecciones.filter((r) => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (
      r.consecutivo?.toLowerCase().includes(t) ||
      r.conductor?.nombre?.toLowerCase().includes(t) ||
      r.estado?.toLowerCase().includes(t)
    );
  });

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('es-CO', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="flex items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/mis-prendas')} className="h-8 w-8 shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <Truck className="h-5 w-5 text-purple-600 shrink-0" />
            <h1 className="text-base font-semibold text-gray-900 truncate">Recolección Ropa Sucia</h1>
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <ClienteActivoSelector
              clientes={clientesAsignados}
              activoId={clienteActivoId}
              onSelect={setClienteActivo}
              variant="inline"
            />
            <Badge className="bg-purple-100 text-purple-800">{totalRecords}</Badge>
          </div>
        </div>
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por consecutivo o conductor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-9 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        {sinClientes ? (
          <Card>
            <CardContent className="p-10 text-center">
              <Building2 className="h-14 w-14 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-700 font-medium">No tienes clientes asignados</p>
              <p className="text-sm text-gray-500 mt-1">Pide al administrador que te asigne uno o varios clientes.</p>
            </CardContent>
          </Card>
        ) : isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto" />
            <p className="text-sm text-gray-500 mt-3">Cargando...</p>
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center">
              <Truck className="h-14 w-14 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No hay recolecciones registradas</p>
            </CardContent>
          </Card>
        ) : (
          filtered.map((r, idx) => {
            const cfg = ESTADO_CONFIG[r.estado] || ESTADO_CONFIG['default'];
            const Icon = cfg.icon;
            const totalPrendas = (r.items || []).reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
            return (
              <motion.div key={r._id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
                <Card
                  className="border-l-4 border-l-purple-400 cursor-pointer hover:shadow-md active:scale-[0.99] transition-all"
                  onClick={() => setSelected(r)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm text-purple-700">{r.consecutivo}</span>
                          <Badge className={`text-xs ${cfg.color}`}>
                            <Icon className="h-3 w-3 mr-1 inline" />{cfg.label}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Package className="h-3 w-3" />{totalPrendas} prendas
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />{formatDate(r.fecha_recoleccion || r.fecha_creacion)}
                          </span>
                          {r.conductor?.nombre && (
                            <span className="flex items-center gap-1">
                              <Truck className="h-3 w-3" />{r.conductor.nombre}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-300 shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              Anterior
            </Button>
            <span className="text-sm text-gray-500">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Siguiente
            </Button>
          </div>
        )}
      </div>

      {/* Modal detalle */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="!fixed !inset-0 !left-0 !top-0 !translate-x-0 !translate-y-0 !max-w-none !w-full !h-full !rounded-none !p-0 !gap-0 !border-0 flex flex-col [&>button]:hidden">
          <DialogTitle className="sr-only">Detalle recolección</DialogTitle>
          {selected && (
            <RecoleccionDetail
              r={selected}
              onClose={() => setSelected(null)}
              formatDateTime={formatDateTime}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const RecoleccionDetail = ({ r, onClose, formatDateTime }) => {
  const cfg = ESTADO_CONFIG[r.estado] || ESTADO_CONFIG['default'];
  const Icon = cfg.icon;
  const totalPrendas = (r.items || []).reduce((s, i) => s + (i.cantidad_alistada || 0), 0);

  return (
    <div className="flex flex-col h-full">
      <div className="bg-purple-600 text-white p-4 flex items-center justify-between shrink-0">
        <div>
          <h2 className="text-lg font-bold">{r.consecutivo}</h2>
          <p className="text-purple-100 text-sm">Recolección ropa sucia</p>
        </div>
        <Button size="icon" variant="ghost" className="text-white hover:bg-purple-700 h-8 w-8" onClick={onClose}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Estado y resumen */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Estado</p>
            <Badge className={`text-xs ${cfg.color}`}>
              <Icon className="h-3 w-3 mr-1 inline" />{cfg.label}
            </Badge>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Total prendas</p>
            <p className="text-lg font-bold text-purple-700">{totalPrendas}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Conductor</p>
            <p className="text-sm font-medium">{r.conductor?.nombre || '-'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Tipo</p>
            <p className="text-sm font-medium capitalize">{r.tipoRecoleccion || 'Ordinaria'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Fecha recolección</p>
            <p className="text-sm font-medium">{formatDateTime(r.fecha_recoleccion)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Fecha llegada</p>
            <p className="text-sm font-medium">{formatDateTime(r.fecha_llegada)}</p>
          </div>
        </div>

        {/* Prendas */}
        <div>
          <h3 className="font-semibold text-sm text-gray-700 mb-2">
            Prendas recogidas ({(r.items || []).length} tipos)
          </h3>
          <div className="space-y-1">
            {(r.items || []).map((item, i) => (
              <div key={i} className="flex items-center justify-between text-sm p-2.5 bg-gray-50 rounded-lg">
                <span className="font-medium truncate flex-1">{item.nombre}</span>
                <span className="text-purple-700 font-semibold ml-3">{item.cantidad_alistada}</span>
              </div>
            ))}
          </div>
        </div>

        {r.observaciones && (
          <div className="bg-yellow-50 rounded-lg p-3">
            <p className="text-xs text-yellow-700 font-semibold mb-1">Observaciones</p>
            <p className="text-sm text-yellow-800">{r.observaciones}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientePrendasRecolecciones;
